import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Send,
  MessageSquare,
  MoreHorizontal,
  FileText,
  Settings,
  LifeBuoy,
  Gift,
  UsersRound,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTeamMe } from '@/features/team/use-team'

type BottomNavItem = {
  readonly label: string
  readonly href: string
  readonly icon: React.ComponentType<{ readonly className?: string }>
}

const PRIMARY_TABS: readonly BottomNavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Campaigns', href: '/campaigns', icon: Send },
  { label: 'Leads', href: '/leads', icon: Users },
  { label: 'Inbox', href: '/inbox', icon: MessageSquare },
] as const

const MORE_ITEMS: readonly BottomNavItem[] = [
  { label: 'Content', href: '/content', icon: FileText },
  { label: 'Settings', href: '/settings', icon: Settings },
  { label: 'Refer & Earn', href: '/affiliate', icon: Gift },
  { label: 'Support', href: '/support', icon: LifeBuoy },
] as const

function NavTab({
  item,
  isActive,
}: {
  readonly item: BottomNavItem
  readonly isActive: boolean
}) {
  return (
    <Link
      to={item.href}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs transition-colors',
        isActive
          ? 'text-cobalt-bright font-medium'
          : 'text-muted-foreground',
      )}
    >
      <item.icon className="h-5 w-5" />
      <span>{item.label}</span>
    </Link>
  )
}

export function BottomNav() {
  const location = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)
  // Client team managers also get the Team console (Enterprise teams).
  const { data: team } = useTeamMe()
  const moreItems: readonly BottomNavItem[] = team?.is_manager
    ? [{ label: 'Team', href: '/team', icon: UsersRound }, ...MORE_ITEMS]
    : MORE_ITEMS

  const isMoreActive = moreItems.some((item) => location.pathname === item.href || location.pathname.startsWith(item.href + '/'))

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 flex border-t border-border bg-background md:hidden">
      {PRIMARY_TABS.map((item) => (
        <NavTab
          key={item.href}
          item={item}
          isActive={
            location.pathname === item.href ||
            (item.href === '/dashboard' && location.pathname === '/')
          }
        />
      ))}

      {/* More menu */}
      <DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs transition-colors',
              isMoreActive
                ? 'text-cobalt-bright font-medium'
                : 'text-muted-foreground',
            )}
          >
            <MoreHorizontal className="h-5 w-5" />
            <span>More</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="mb-2">
          {moreItems.map((item) => (
            <DropdownMenuItem key={item.href} asChild>
              <Link to={item.href} className="flex items-center gap-2">
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  )
}
